import * as React from 'react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import useLocation from 'hooks/location/use-location';
import { useSupportData } from 'hooks/use-support-data';
import { useUserOrders } from 'hooks/use-user-orders';

import { IUserOrderFragment } from '../../../generated/rbi-graphql';
import useAuth from '../../../hooks/auth/use-auth';
import { useOrderDetails } from '../../../hooks/use-order-details';
import { routes } from '../../../utils/routing';
import { ErrorView } from '../error-view';
import { LoadingView } from '../loading-view';
import { InnerBox, PageWrap } from '../support-form-v2.styled';

import { SupportV2OrdersListView } from './support-v2-orders-list-view';
import { getDropdownOptions, groupOrdersByYearAndMonth } from './utils';

/**
 * Displays list of a users recent orders for them to select from
 */
const SupportV2OrdersListPage: FC = () => {
  const { formatMessage, locale } = useIntl();
  const { location } = useLocation();
  const query = new URLSearchParams(location.search);
  const [searchQuery] = useSearchParams();
  const subcategoryId = searchQuery.get('subcategoryId');
  const categoryId = searchQuery.get('categoryId');
  const preselectedOrderId = searchQuery.get('orderId');

  const {
    order: preselectedOrder,
    loading: isPreselectedOrderLoading,
    error: preselectedOrderError,
  } = useOrderDetails(preselectedOrderId || null);

  const { isAuthenticated: isAuthenticatedFn, setOriginLocation } = useAuth();
  const isAuthenticated = isAuthenticatedFn();
  const { orders, loading: isLoadingOrders } = useUserOrders({
    skipQuery: Boolean(preselectedOrderId),
  });
  const pageTitleText = formatMessage({ id: 'recentOrders' });
  const [selectedYearAndMonth, setSelectedYearAndMonth] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<IUserOrderFragment | IHistoricalOrder | null>(
    null
  );

  const {
    isSupportDataLoading,
    deliveryNeverArriveSubcategory,
    deliveryMissingItemsSubcategory,
  } = useSupportData();

  const navigate = useNavigate();

  useEffect(() => {
    if (preselectedOrder) {
      setSelectedOrder(preselectedOrder);
    }
  }, [preselectedOrder, setSelectedOrder]);

  /** When an order is selected and eligibility has been determined. */
  useEffect(() => {
    if (isAuthenticated && !isPreselectedOrderLoading) {
      const orderId = selectedOrder?.rbiOrderId || preselectedOrderId;
      const newQuery = new URLSearchParams({
        ...(categoryId && { categoryId }),
        ...(subcategoryId && { subcategoryId }),
        ...(orderId && { orderId }),
      } as Record<string, string>).toString();

      if (selectedOrder) {
        const missingItemsSubcategoryId = deliveryMissingItemsSubcategory?._id;
        const neverArrivedSubcategoryId = deliveryNeverArriveSubcategory?._id;
        // If orderId is in route, going back would send them forward again.
        const isOrderIdPreselected = Boolean(preselectedOrderId);

        const orderIssueQuery = new URLSearchParams({
          ...(categoryId && { categoryId }),
          /**
           * Still passing subcategory ID for furture combination
           * of customer initiated refund pages.
           */
          ...(subcategoryId && { subcategoryId }),
        } as Record<string, string>).toString();

        if (!preselectedOrder?.order?.refund && subcategoryId === missingItemsSubcategoryId) {
          return navigate(`${routes.missingItems}/${selectedOrder.rbiOrderId}?${orderIssueQuery}`, {
            replace: isOrderIdPreselected,
          });
        } else if (
          !preselectedOrder?.order?.refund &&
          subcategoryId === neverArrivedSubcategoryId
        ) {
          return navigate(`${routes.missingOrder}/${selectedOrder.rbiOrderId}?${orderIssueQuery}`, {
            replace: isOrderIdPreselected,
          });
        } else {
          return navigate(`${routes.supportForm}?${newQuery}`, {
            replace: isOrderIdPreselected,
          });
        }
      }
    }
  }, [
    selectedOrder,
    deliveryMissingItemsSubcategory,
    deliveryNeverArriveSubcategory,
    navigate,
    subcategoryId,
    query,
    categoryId,
    isAuthenticated,
    preselectedOrderId,
    isPreselectedOrderLoading,
    preselectedOrder,
  ]);

  /**
   * If the user has no orders, take them straight to the support form.
   */
  useEffect(() => {
    if (!isLoadingOrders && orders?.length === 0) {
      const newQuery = new URLSearchParams({
        ...(categoryId && { categoryId }),
        ...(subcategoryId && { subcategoryId }),
      } as Record<string, string>).toString();

      navigate(`${routes.supportForm}?${newQuery}`, { replace: true });
    }
  }, [categoryId, isLoadingOrders, navigate, subcategoryId, orders]);

  const ordersGroupedByYearAndMonth = useMemo(() => groupOrdersByYearAndMonth(orders), [orders]);
  const dropdownOptions = useMemo(() => getDropdownOptions(ordersGroupedByYearAndMonth, locale), [
    locale,
    ordersGroupedByYearAndMonth,
  ]);

  const onClickSignin = () => {
    setOriginLocation(location.pathname + location.search);
    navigate(routes.signIn);
  };

  /**
   * set this value after the orders have loaded and `dropdownOptions` has values
   */
  useEffect(() => {
    if (dropdownOptions?.length) {
      setSelectedYearAndMonth(dropdownOptions[0].value);
    }
  }, [dropdownOptions]);

  const handleChangeYearAndMonth = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setSelectedYearAndMonth(event.target.value),
    [setSelectedYearAndMonth]
  );

  if (
    isLoadingOrders ||
    isSupportDataLoading ||
    /**
     * The page should stay in a loading state waiting for
     * a redirect. order or order loading in case it errors.
     */
    preselectedOrder ||
    isPreselectedOrderLoading
  ) {
    return <LoadingView />;
  }

  if ((isAuthenticated && orders?.length === 0) || preselectedOrderError) {
    return <ErrorView message={formatMessage({ id: 'looksLikeWereExperiencingIssues' })} />;
  }

  return (
    <PageWrap>
      <InnerBox>
        <BackArrowHistory />
        <Helmet title={pageTitleText} />
        <SupportV2OrdersListView
          dropdownOptions={dropdownOptions}
          items={ordersGroupedByYearAndMonth[selectedYearAndMonth] || []}
          onYearAndMonthChange={handleChangeYearAndMonth}
          selectedYearAndMonth={selectedYearAndMonth}
          isAuthenticated={isAuthenticated}
          onClickSignin={onClickSignin}
          onSelectOrder={setSelectedOrder}
        />
      </InnerBox>
    </PageWrap>
  );
};

export default SupportV2OrdersListPage;
